import React, { useState } from 'react'
import { FormControl, Select, MenuItem, ListSubheader, TextField, InputAdornment } from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import { makeStyles } from '@material-ui/core/styles'
import { useTranslate } from 'react-admin'
import competitionFilterStyles from './competitionFilter.styles'
import matchesSearch from './matchesSearch'

const useStyles = makeStyles(competitionFilterStyles)

// Select clones its children and injects onClick/role/selected etc. Dropping those props
// keeps clicks inside the search box from selecting a value and closing the menu.
const SearchHeader = ({ query, onQueryChange, placeholder, className }) => (
  <ListSubheader className={className}>
    <TextField
      autoFocus
      fullWidth
      size='small'
      placeholder={placeholder}
      value={query}
      onChange={(event) => onQueryChange(event.target.value)}
      // Keep the menu's type-ahead/arrow handling from stealing keystrokes; Escape still closes.
      onKeyDown={(event) => event.key !== 'Escape' && event.stopPropagation()}
      InputProps={{
        startAdornment: (
          <InputAdornment position='start'>
            <SearchIcon fontSize='small' />
          </InputAdornment>
        ),
      }}
    />
  </ListSubheader>
)

const NoResults = ({ label, className }) => (
  <ListSubheader className={className}>{label}</ListSubheader>
)

const CompetitionFilter = ({ competitions, selectedCompetition, onCompetitionChange, className }) => {
  const classes = useStyles()
  const translate = useTranslate()
  const [query, setQuery] = useState('')

  const handleChange = (event) => {
    const competitionId = event.target.value
    if (competitionId === undefined) return
    const competition = competitions?.find((c) => c.id === competitionId)
    onCompetitionChange(competition || null)
  }

  const visibleCount = competitions?.filter((c) => matchesSearch(c.name, query)).length

  return (
    <FormControl className={`${classes.formControl} ${className}`}>
      <Select
        labelId='competition-filter-label'
        id='competition-filter'
        value={selectedCompetition?.id || ''}
        onChange={handleChange}
        onClose={() => setQuery('')}
        displayEmpty
        disabled={!competitions}
        MenuProps={{ autoFocus: false, getContentAnchorEl: null, anchorOrigin: { vertical: 'bottom', horizontal: 'left' } }}
      >
        <SearchHeader
          query={query}
          onQueryChange={setQuery}
          placeholder={translate('ra.action.search')}
          className={classes.searchHeader}
        />
        <MenuItem value=''>
          <em>All Competitions</em>
        </MenuItem>
        {competitions && competitions.map((competition) => (
          // Non-matching items stay mounted (hidden) so the Select can still render the selected value.
          <MenuItem
            key={competition.id}
            value={competition.id}
            style={matchesSearch(competition.name, query) ? undefined : { display: 'none' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {competition.logoUrl && (
                <img
                  src={competition.logoUrl}
                  alt={competition.name}
                  style={{ width: 20, height: 20, objectFit: 'contain' }}
                />
              )}
              <span>{competition.name}</span>
            </div>
          </MenuItem>
        ))}
        {query && visibleCount === 0 && (
          <NoResults label={translate('ra.navigation.no_results')} className={classes.noResults} />
        )}
      </Select>
    </FormControl>
  )
}

export default CompetitionFilter
